import { Formik, FormikProps } from "formik";
import isEqual from "lodash/isEqual";
import { LimitType, NotificationTriggerInput } from "model/schema/globalTypes";
import { forwardRef, useRef } from "react";
import * as Yup from "yup";
import { notificationTriggersValidationSchema } from "./helper/serviceProductValidation";
import NotificationSettings from "./NotificationSettings";
import { normalizeErrors } from "./notificationtriggerhelper";

interface ServiceProductNotificationEditProps {
  notificationTriggers: NotificationTriggerInput[];
  limitType: LimitType;
  isReadOnly: boolean;
  emailFieldWidth: number;
  onEditToggle?: (readOnly: boolean) => void;
  onSave?: (notificationTriggers: NotificationTriggerInput[]) => void;
  loading?: boolean;
  errorMessage?: string;
}

const ServiceProductNotificationEdit = forwardRef<
  FormikProps<{ notificationTriggers: NotificationTriggerInput[] }> | null,
  ServiceProductNotificationEditProps
>(
  (
    {
      notificationTriggers,
      limitType,
      isReadOnly,
      emailFieldWidth,
      onEditToggle,
      onSave,
      loading = false,
      errorMessage
    },
    ref
  ) => {
    const initialStateRef = useRef<NotificationTriggerInput[] | null>(null);
    return (
      <Formik
        initialValues={{ notificationTriggers }}
        enableReinitialize
        onSubmit={(values, formikHelpers) => {
          if (onSave) onSave(values.notificationTriggers);
        }}
        innerRef={ref}
        validationSchema={Yup.object({
          notificationTriggers: notificationTriggersValidationSchema
        })}
      >
        {({ values, setFieldValue, errors, validateForm }) => {
          const handleEditToggle = () => {
            if (onEditToggle) {
              if (!isReadOnly) {
                // Leaving edit mode, restore initial state
                if (initialStateRef.current) {
                  setFieldValue(
                    "notificationTriggers",
                    initialStateRef.current
                  );
                  // Ensure validation runs after state update using microtask
                  Promise.resolve().then(() => {
                    validateForm();
                  });
                  initialStateRef.current = null;
                }
              } else {
                // Entering edit mode, store initial state
                initialStateRef.current = [...values.notificationTriggers];
              }
              onEditToggle(!isReadOnly);
            }
          };
          return (
            <form
              onSubmit={e => {
                e.preventDefault();
                if (onSave) onSave(values.notificationTriggers);
              }}
            >
              <NotificationSettings
                notificationTriggers={values.notificationTriggers}
                limitType={limitType}
                isReadOnly={isReadOnly}
                emailFieldWidth={emailFieldWidth}
                onEditToggle={handleEditToggle}
                onEmailChange={(email, idx) => {
                  setFieldValue(`notificationTriggers.${idx}.email`, email);
                }}
                onThresholdChange={(payload, idx) => {
                  const trigger = values.notificationTriggers?.[idx];
                  if (!trigger) return;
                  const currentThresholds = trigger.thresholds;
                  const newThresholds = payload.value;
                  if (isEqual(currentThresholds, newThresholds)) return;
                  setFieldValue(
                    `notificationTriggers.${idx}.thresholds`,
                    newThresholds
                  );
                }}
                onAddRow={() => {
                  setFieldValue("notificationTriggers", [
                    ...(values.notificationTriggers ?? []),
                    { email: "", thresholds: [] }
                  ]);
                }}
                onDeleteRow={idx => {
                  setFieldValue(
                    "notificationTriggers",
                    (values.notificationTriggers ?? []).filter(
                      (_, i) => i !== idx
                    )
                  );
                }}
                loading={loading}
                errorMessage={errorMessage}
                errors={normalizeErrors(errors)}
              />
            </form>
          );
        }}
      </Formik>
    );
  }
);

export default ServiceProductNotificationEdit;
